import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
} from '@folio/components';
import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFeatureFlag, useKorintProductConfig } from 'korint-sdk/react';
import { formatNumber } from '@folio/lib';
import { useInvoicingFrequencyLabels } from '@folio/constants';
import { QuoteContext } from '../shared-quote/QuoteContext';
import { getPerilIcon } from '../shared-quote/perilIcon';
import { normalizeValueForComparison } from '../../components/forms/form-builder/conditionalVisibilityUtils';
import { AlertCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ConfigQuoteAvailableWarrantyDto,
  ConfigQuoteChosenWarrantyDto,
  ConfigQuoteShowWhenMultipleValuesDto,
  ConfigQuoteShowWhenSingleValueDto,
  ConfigQuoteShowWhenNoValueDto,
} from 'korint-sdk';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { ThemeIcon } from '@folio/components/ui/theme-icon';

type ShowWhenDto =
  | ConfigQuoteShowWhenSingleValueDto
  | ConfigQuoteShowWhenMultipleValuesDto
  | ConfigQuoteShowWhenNoValueDto;

function evaluateShowWhenCondition(
  referencedChosenPeril: ConfigQuoteChosenWarrantyDto | undefined,
  showWhen: ShowWhenDto,
): boolean {
  if ('value' in showWhen) {
    const sourceValue = referencedChosenPeril?.option;
    const compareValue = Number(showWhen.value);
    const normalizedValue = normalizeValueForComparison(sourceValue, compareValue);

    switch (showWhen.condition) {
      case 'equals':
        return sourceValue === showWhen.value;
      case 'notEquals':
        return sourceValue !== showWhen.value;
      case 'greaterThan':
        return (
          typeof normalizedValue === 'number' &&
          typeof compareValue === 'number' &&
          !Number.isNaN(compareValue) &&
          !Number.isNaN(normalizedValue) &&
          normalizedValue > compareValue
        );
      case 'lessThan':
        return (
          typeof normalizedValue === 'number' &&
          typeof compareValue === 'number' &&
          !Number.isNaN(compareValue) &&
          !Number.isNaN(normalizedValue) &&
          normalizedValue < compareValue
        );
      case 'greaterThanOrEquals':
        return (
          typeof normalizedValue === 'number' &&
          typeof compareValue === 'number' &&
          !Number.isNaN(compareValue) &&
          !Number.isNaN(normalizedValue) &&
          normalizedValue >= compareValue
        );
      case 'lessThanOrEquals':
        return (
          typeof normalizedValue === 'number' &&
          typeof compareValue === 'number' &&
          !Number.isNaN(compareValue) &&
          !Number.isNaN(normalizedValue) &&
          normalizedValue <= compareValue
        );
      default:
        return false;
    }
  }
  if ('values' in showWhen) {
    const option = referencedChosenPeril?.option;
    switch (showWhen.condition) {
      case 'in':
        return option !== undefined && showWhen.values.includes(option);
      case 'notIn':
        return option === undefined || !showWhen.values.includes(option);
      default:
        return false;
    }
  }
  switch (showWhen.condition) {
    case 'exists':
      return referencedChosenPeril != null;
    case 'notExists':
      return referencedChosenPeril == null;
    default:
      return false;
  }
}

function isShowWhenConditionVerified(
  chosenPerils: ConfigQuoteChosenWarrantyDto[] | undefined,
  availablePeril: ConfigQuoteAvailableWarrantyDto,
): boolean {
  const showWhen = availablePeril.showWhen;
  if (showWhen === undefined) return true;

  if (showWhen.fieldKey === 'perils.id') {
    const referencedChosenPeril = chosenPerils?.find(p => p.id === showWhen.fieldValue);
    return evaluateShowWhenCondition(referencedChosenPeril, showWhen);
  }

  return false;
}

export function QuoteOptionsSelectModule() {
  const { t } = useTranslation();
  const invoicingFrequencyLabels = useInvoicingFrequencyLabels();

  const form = useFormContext();

  const { productsConfig } = useKorintProductConfig();

  const { policy, asset, customer, quoteConfig, calculatedQuotes } = useContext(QuoteContext);

  const { delegatedQuoteModuleConfig } = productsConfig[policy?.productId!] || {};

  const lockedPeril = delegatedQuoteModuleConfig?.lockedPeril;
  const isLockedPerilOn = useFeatureFlag(lockedPeril?.featureFlag, policy?.productId);
  // Backend strips the peril on new business only; locking an MTA would drop it from a contract
  const lockedPerilId =
    isLockedPerilOn && policy?.branchType === 'NEW_BUSINESS' ? lockedPeril?.id : undefined;

  const insurer = form.watch('insurer');
  const tier = form.watch('tier');

  const invoicingFrequency =
    form.watch('invoicingConfig.frequency') ?? policy?.invoicingConfig?.frequency;
  const invoicingFrequencyLabel = invoicingFrequency
    ? (invoicingFrequencyLabels[invoicingFrequency] ?? invoicingFrequency)
    : '';

  const quote = calculatedQuotes.find(q => q.tier === tier && q.insurer === insurer)?.quote;

  const unavailablePerils = quote?.assets?.[0]?.options.perils?.filter(p => p.available === false);

  const optionalPerils =
    quoteConfig?.availableConfigs
      .find(c => c.tier === tier)
      ?.perils?.filter(p => !p.required || !!p.options)
      .filter(p => isShowWhenConditionVerified(quote?.assets?.[0]?.options.perils, p)) ?? [];

  const chosenPerils = quote?.assets?.[0]?.options.perils;
  useEffect(() => {
    const tierPerils = quoteConfig?.availableConfigs.find(c => c.tier === tier)?.perils ?? [];
    for (const p of tierPerils) {
      const isHidden = p.showWhen && !isShowWhenConditionVerified(chosenPerils, p);
      if (
        (isHidden || p.id === lockedPerilId) &&
        (form.getValues(`peril-${p.id}`) ?? 'false') !== 'false'
      ) {
        form.setValue(`peril-${p.id}`, 'false');
      }
    }
  }, [chosenPerils, tier, quoteConfig, form, lockedPerilId]);

  if (optionalPerils.length === 0 || delegatedQuoteModuleConfig?.showPerilOptions === false) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div>{t('perilOptionsCard.title')}</div>
          <div className="text-sm text-gray-600">{t('perilOptionsCard.description')}</div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Stack aria-label={t('perilOptionsCard.title')} gap={3} className="lg:grid lg:grid-cols-2">
          {optionalPerils?.map(p => {
            const amountNumberOrAccessor = delegatedQuoteModuleConfig?.perilAmounts?.[p.id];

            const optionsFilter = delegatedQuoteModuleConfig?.perilOptionsFilter?.[p.id];
            const displayedOptions =
              p.options && optionsFilter
                ? optionsFilter({ options: p.options, policy, customer })
                : p.options;

            const isLocked = p.id === lockedPerilId;
            const unavailabilityMessage = isLocked
              ? t('perilOptionsCard.unavailable')
              : unavailablePerils?.find(({ id }) => id === p.id)?.unavailabilityMessage;

            const amount =
              typeof amountNumberOrAccessor === 'function'
                ? amountNumberOrAccessor({
                    policyQuote: quote?.policy,
                    assetQuote: quote?.assets?.find(({ entityId }) => entityId === asset?.id),
                    assetQuotes: quote?.assets,
                  })
                : amountNumberOrAccessor;

            const PerilIcon = getPerilIcon(p.id);
            const isPerilEnabled =
              Boolean(form.watch(`peril-${p.id}`)) && form.watch(`peril-${p.id}`) !== 'false';

            return (
              <Group
                key={p.id}
                justify="between"
                className="h-full border rounded-lg p-4 hover:bg-accent/50"
              >
                <Group gap={3} className="min-w-0">
                  <ThemeIcon
                    size="md"
                    radius="md"
                    tone={isPerilEnabled ? 'primary' : 'muted'}
                    className="transition-colors"
                  >
                    <PerilIcon />
                  </ThemeIcon>
                  <Stack gap={2} className="min-w-0">
                    <div className="font-bold text-sm">
                      {t(`${policy?.productId}.perils.${p.id}.label`, { defaultValue: p.id })}
                    </div>
                    {Boolean(t([`${policy?.productId}.perils.${p.id}.description`, 'empty'])) && (
                      <div className="text-gray-600 text-sm">
                        {t(`${policy?.productId}.perils.${p.id}.description`)}
                      </div>
                    )}
                    {unavailabilityMessage && (
                      <Group gap={2} className="text-red-600 text-sm">
                        <AlertCircleIcon className="w-4 h-4" />
                        {unavailabilityMessage}
                      </Group>
                    )}
                  </Stack>
                </Group>

                <Group gap={2} className="shrink-0">
                  {amountNumberOrAccessor !== undefined && (
                    <div className="font-bold text-xl my-5">
                      {amount !== undefined ? (
                        formatNumber(amount / 100)
                      ) : (
                        <Skeleton className="w-20 h-5 inline-flex" />
                      )}
                      €
                      {invoicingFrequencyLabel && (
                        <Text inline weight="normal" size="sm" className="text-gray-400">
                          / {invoicingFrequencyLabel}
                        </Text>
                      )}
                    </div>
                  )}
                  <div>
                    {p.options ? (
                      <Select
                        disabled={isLocked}
                        onValueChange={v => form.setValue(`peril-${p.id}`, v)}
                        defaultValue={form.watch(`peril-${p.id}`) ?? 'false'}
                        key={form.watch(`peril-${p.id}`) ?? 'false'}
                      >
                        <SelectTrigger name={p.id}>
                          <SelectValue placeholder={t('fields.select.placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {!p.required && (
                            <SelectItem value="false">{t('options.false')}</SelectItem>
                          )}
                          {displayedOptions?.map(option => (
                            <SelectItem key={option} value={option}>
                              {t([
                                `${policy?.productId}.perils.${p.id}.options.${option}`,
                                `options.${option}`,
                              ])}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Switch
                        disabled={isLocked}
                        checked={isPerilEnabled}
                        onCheckedChange={checked =>
                          form.setValue(`peril-${p.id}`, checked ? 'true' : 'false')
                        }
                      />
                    )}
                  </div>
                </Group>
              </Group>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}
